/* ============================================================
   <product-grid> custom element
   Handles:
    - Async fetching of Shopify product recommendations
    - Horizontal carousel scrolling via arrow buttons
    - Quick-add from product cards
   ============================================================ */

class ProductGrid extends HTMLElement {
  connectedCallback() {
    this._initCarousel();
    this._initQuickAdd();

    const url = this.dataset.recommendationsUrl;
    if (url) this._loadRecommendations(url);
  }

  /* ------------------------------------------------------------
     Recommendations (async fetch + section replacement)
     ------------------------------------------------------------ */
  async _loadRecommendations(url) {
    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) {
        this.remove();
        return;
      }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('product-grid');

      if (!incoming || !incoming.querySelector('[data-pg-grid]')) {
        // No recommendations returned — hide the section
        this.remove();
        return;
      }

      this.innerHTML = incoming.innerHTML;
      this.classList.remove('pg-section--pending');
      this._initCarousel();
      this._initQuickAdd();
    } catch (err) {
      // Swallow errors — just hide the section
      this.remove();
    }
  }

  /* ------------------------------------------------------------
     Carousel
     ------------------------------------------------------------ */
  _initCarousel() {
    this.grid = this.querySelector('[data-pg-grid]');
    this.prevBtn = this.querySelector('[data-pg-prev]');
    this.nextBtn = this.querySelector('[data-pg-next]');

    if (!this.grid) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this._scrollBy(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this._scrollBy(1));
    }

    this._onScroll = this._updateNavState.bind(this);
    this.grid.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll);

    this._updateNavState();
  }

  _scrollBy(direction) {
    if (!this.grid) return;
    const items = this.grid.querySelectorAll('.pg-grid__item');
    if (!items.length) return;
    const firstItem = items[0];
    const itemWidth = firstItem.getBoundingClientRect().width;
    const styles = getComputedStyle(this.grid);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const step = (itemWidth + gap) * Math.max(1, Math.floor(this.grid.clientWidth / (itemWidth + gap)));
    this.grid.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  _updateNavState() {
    if (!this.grid) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.grid;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    if (this.prevBtn) this.prevBtn.toggleAttribute('aria-disabled', atStart);
    if (this.nextBtn) this.nextBtn.toggleAttribute('aria-disabled', atEnd);
  }

  /* ------------------------------------------------------------
     Quick add to cart
     ------------------------------------------------------------ */
  _initQuickAdd() {
    const buttons = this.querySelectorAll('[data-pg-quick-add]');
    buttons.forEach((btn) => {
      if (btn._pgBound) return;
      btn._pgBound = true;
      btn.addEventListener('click', (e) => this._onQuickAdd(e, btn));
    });
  }

  async _onQuickAdd(e, btn) {
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    const labelEl = btn.querySelector('span');
    const originalLabel = labelEl ? labelEl.textContent : '';

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || btn.dataset.pgErrorLabel || 'Could not add to cart.');
      }

      btn.classList.add('is-added');
      if (labelEl) labelEl.textContent = btn.dataset.pgAddedLabel || 'Added';
      document.dispatchEvent(new CustomEvent('cart:added'));
      ProductGrid._toast(btn.dataset.pgAddedToast || 'Added to cart');

      setTimeout(() => {
        btn.classList.remove('is-added');
        if (labelEl) labelEl.textContent = originalLabel;
      }, 1800);
    } catch (err) {
      ProductGrid._toast(err.message || btn.dataset.pgErrorLabel || 'Could not add.', 'error');
    } finally {
      btn.classList.remove('is-loading');
    }
  }

  static _toast(message, variant = '') {
    let el = document.querySelector('.pg-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'pg-toast';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.remove('pg-toast--error');
    if (variant === 'error') el.classList.add('pg-toast--error');
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(ProductGrid._toastTimer);
    ProductGrid._toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
    }, 2000);
  }
}

if (!customElements.get('product-grid')) {
  customElements.define('product-grid', ProductGrid);
}
